import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Download, ExternalLink, FileText, Maximize2, Printer, X } from "lucide-react";

import { Button } from "@/components/ui/Button";
import { cn } from "@/lib/cn";

export interface PdfPreviewProps {
  src: string;
  filename: string;
  title?: string;
  copies?: readonly { value: string; label: string }[];
  copyValue?: string;
  onCopyChange?: (value: string) => void;
}

export function PdfPreview({
  src,
  filename,
  title = "PDF preview",
  copies,
  copyValue,
  onCopyChange,
}: PdfPreviewProps) {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const fullscreenIframeRef = useRef<HTMLIFrameElement>(null);
  const [expanded, setExpanded] = useState(false);
  // One-shot mobile check, decided BEFORE the first paint. Chrome Android's
  // inline PDF viewer often shows "Cannot display this PDF" — on phones we
  // skip the iframe entirely and offer "View PDF" in a new tab instead.
  // (Deciding in an effect used to mount the iframe for one frame, which
  // still fired the PDF request on every phone view.)
  const [isMobile] = useState(
    () =>
      typeof window !== "undefined" &&
      !!window.matchMedia &&
      window.matchMedia("(max-width: 639px)").matches,
  );

  const printPdf = useCallback(() => {
    const w = (expanded ? fullscreenIframeRef : iframeRef).current?.contentWindow;
    if (!w) return;
    w.focus();
    w.print();
  }, [expanded]);

  // Esc closes the fullscreen view.
  useEffect(() => {
    if (!expanded) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setExpanded(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [expanded]);

  if (isMobile) {
    return (
      <div className="space-y-3">
        <a
          href={src}
          target="_blank"
          rel="noopener"
          className={cn(
            "block rounded-lg border border-border bg-white p-6",
            "flex flex-col items-center text-center",
            "hover:border-accent-500 active:bg-neutral-50",
          )}
        >
          <FileText className="size-14 text-navy-300 mb-3" aria-hidden="true" />
          <div className="text-sm font-medium text-navy-900 break-all max-w-full">{filename}</div>
          <div className="mt-1 text-xs text-neutral-500">Tap to open PDF in a new tab.</div>
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent-600 px-4 py-2.5 min-h-[44px] text-sm font-medium text-white">
            <ExternalLink className="size-4" />
            View PDF
          </div>
        </a>
        <div className="flex flex-wrap items-center gap-2">
          <a href={src} download={filename}>
            <Button variant="secondary" iconLeft={<Download className="size-4" />}>
              Download
            </Button>
          </a>
          {copies?.length ? (
            <div className="ml-auto flex flex-wrap gap-2">
              {copies.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => onCopyChange?.(c.value)}
                  className={cn(
                    "rounded-md border px-3 py-2 min-h-[40px] text-xs text-navy-800",
                    copyValue === c.value
                      ? "border-accent-500 bg-accent-50"
                      : "border-border hover:border-navy-300",
                  )}
                >
                  {c.label}
                </button>
              ))}
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      <div
        className="rounded-lg border border-border bg-white overflow-hidden"
        style={{ height: "min(1100px, calc(100vh - 220px))" }}
      >
        {/*
          key={src} forces a remount when the URL changes (e.g. copy
          consignor → driver). Without it Chrome's embedded PDF plugin
          ignores in-place src changes and keeps showing the first PDF.
        */}
        <iframe key={src} ref={iframeRef} title={title} src={src} className="size-full" />
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Button
          type="button"
          variant="secondary"
          iconLeft={<Maximize2 className="size-4" />}
          onClick={() => setExpanded(true)}
        >
          Expand
        </Button>
        <a href={src} download={filename}>
          <Button variant="secondary" iconLeft={<Download className="size-4" />}>
            Download
          </Button>
        </a>
        <Button
          type="button"
          variant="secondary"
          iconLeft={<Printer className="size-4" />}
          onClick={printPdf}
        >
          Print
        </Button>
        {copies?.length ? (
          <div className="ml-auto flex flex-wrap gap-2">
            {copies.map((c) => (
              <button
                key={c.value}
                type="button"
                onClick={() => onCopyChange?.(c.value)}
                className={cn(
                  "rounded-md border px-2 py-1.5 text-xs text-navy-800",
                  copyValue === c.value
                    ? "border-accent-500 bg-accent-50"
                    : "border-border hover:border-navy-300",
                )}
              >
                {c.label}
              </button>
            ))}
          </div>
        ) : null}
      </div>

      {expanded &&
        createPortal(
          <div
            role="dialog"
            aria-modal="true"
            aria-label={title}
            className="fixed inset-0 z-[1100] flex flex-col bg-black/70 p-4"
            onClick={(e) => {
              if (e.target === e.currentTarget) setExpanded(false);
            }}
          >
            <div className="mx-auto flex w-full max-w-6xl flex-1 flex-col overflow-hidden rounded-lg bg-white shadow-2xl">
              <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-2">
                <div className="text-sm font-medium text-navy-900 truncate">{title}</div>
                <div className="flex items-center gap-2">
                  {copies?.length && copyValue
                    ? copies.map((c) => (
                        <button
                          key={c.value}
                          type="button"
                          onClick={() => onCopyChange?.(c.value)}
                          className={cn(
                            "rounded-md border px-2 py-1.5 text-xs text-navy-800",
                            copyValue === c.value
                              ? "border-accent-500 bg-accent-50"
                              : "border-border hover:border-navy-300",
                          )}
                        >
                          {c.label}
                        </button>
                      ))
                    : null}
                  <a href={src} download={filename}>
                    <Button variant="secondary" iconLeft={<Download className="size-4" />}>
                      Download
                    </Button>
                  </a>
                  <Button
                    type="button"
                    variant="secondary"
                    iconLeft={<Printer className="size-4" />}
                    onClick={printPdf}
                  >
                    Print
                  </Button>
                  <button
                    type="button"
                    onClick={() => setExpanded(false)}
                    className="inline-flex size-9 items-center justify-center rounded-md border border-border text-neutral-600 hover:text-navy-800"
                    aria-label="Close preview"
                  >
                    <X className="size-4" />
                  </button>
                </div>
              </div>
              <iframe
                key={src}
                ref={fullscreenIframeRef}
                title={`${title} (fullscreen)`}
                src={src}
                className="size-full flex-1"
              />
            </div>
          </div>,
          document.body,
        )}
    </div>
  );
}
